import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { environment } from '@environments/environment'
import { Observable } from 'rxjs'
import { UserIdentifier, CreateIdentifierResponse } from '../types/user-identifier'
import { Authenticator, MFA_FACTOR_TYPE } from '../types/mfa.types'
import { Response } from '@app/v2/shared/types/generic-response.type'

/** Maps MFA_FACTOR_TYPE to API channel string */
const MFA_CHANNEL_MAP: Record<MFA_FACTOR_TYPE, string> = {
  [MFA_FACTOR_TYPE.APP]: 'APP',
  [MFA_FACTOR_TYPE.EMAIL]: 'EMAIL',
  // Backend enum is `SMS` (not `PHONE`)
  [MFA_FACTOR_TYPE.PHONE]: 'SMS'
}

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  constructor(private http: HttpClient) {}

  /**
   * @description Creates a new user identifier (email or phone)
   * @param {type: 'EMAIL' | 'PHONE'; identifier: string; is_primary: boolean} identifierData - The identifier data
   * @returns Observable
   */
  public createIdentifier(identifierData: { type: 'EMAIL' | 'PHONE'; identifier: string; is_primary: boolean }): Observable<Response<CreateIdentifierResponse>> {
    return this.http.post<Response<CreateIdentifierResponse>>(`${environment.v2.baseUrl}users/identifiers/create`, identifierData, {
      context: new HttpContext().set(BypassErrorHandling, true)
    })
  }

  /**
   * Get all user identifiers
   * @returns Observable
   */
  public getIdentifiers(): Observable<Response<UserIdentifier[]>> {
    return this.http.get<Response<UserIdentifier[]>>(`${environment.v2.baseUrl}users/identifiers`, {
      // Allow global interceptors to handle 401/403 and restart SSO flow.
      context: new HttpContext()
    })
  }

  /**
   * Update an identifier
   * @param identifierId identifier id
   * @param identifierData identifier data
   * @returns Observable
   */
  public updateIdentifier(identifierId: number, identifierData: { type: 'EMAIL' | 'PHONE'; identifier: string; is_primary: boolean }): Observable<Response<UserIdentifier>> {
    return this.http.patch<Response<UserIdentifier>>(`${environment.v2.baseUrl}users/identifiers/${identifierId}/update`, identifierData, {
      context: new HttpContext().set(BypassErrorHandling, true)
    })
  }

  /**
   * Delete an identifier
   * @param identifierId identifier id
   * @returns Observable
   */
  public deleteIdentifier(identifierId: number): Observable<UserIdentifier> {
    return this.http.delete<UserIdentifier>(`${environment.v2.baseUrl}users/identifiers/${identifierId}/delete`, {
      context: new HttpContext().set(BypassErrorHandling, true)
    })
  }

  /**
   * Change the user password
   * @paramm { current_password?: string; mfa_code?: string; email_code?: string; new_password: string; new_password_confirmation: string } data - The data to change the password
   * @returns Observable
   */
  public changePassword(data: {
    current_password?: string
    mfa_code?: string
    email_code?: string
    new_password: string
    new_password_confirmation: string
  }): Observable<Response<void>> {
    return this.http.post<Response<void>>(`${environment.v2.baseUrl}auth/change-password`, data, {
      context: new HttpContext().set(BypassErrorToast, true).set(BypassErrorHandling, true)
    })
  }

  /** Sends email OTP for password change fallback (password-less users). */
  public sendChangePasswordEmailOtp(): Observable<Response<{ email_sent: boolean }>> {
    return this.http.post<Response<{ email_sent: boolean }>>(
      `${environment.v2.baseUrl}auth/change-password/send-email-otp`,
      {},
      { context: new HttpContext().set(BypassErrorHandling, true) }
    )
  }

  /**
   * Sends an MFA OTP to the current user for step-up flows (password change).
   * - EMAIL/SMS: sends OTP
   * - APP: nothing is sent, but MFA is still required
   */
  public sendMfaOtp(): Observable<Response<{ mfa_required: boolean; mfa_channel?: string }>> {
    return this.http.post<Response<{ mfa_required: boolean; mfa_channel?: string }>>(
      `${environment.v2.baseUrl}auth/mfa/send-otp`,
      {},
      { context: new HttpContext().set(BypassErrorHandling, true) }
    )
  }

  /**
   * Starts the MFA enable flow. POST auth/mfa/enable returns QR + recovery codes for APP; triggers send for EMAIL/PHONE.
   */
  public enableMFA(factor: MFA_FACTOR_TYPE): Observable<Response<Authenticator>> {
    return this.http.post<Response<Authenticator>>(
      `${environment.v2.baseUrl}auth/mfa/enable`,
      {
        mfa_enabled: true,
        mfa_channel: MFA_CHANNEL_MAP[factor]
      },
      { context: new HttpContext().set(BypassErrorHandling, true) }
    )
  }

  /**
   * Confirms MFA setup with the code from the authenticator app or email/phone OTP. POST auth/mfa/confirm.
   * Call after user enters the 6-digit code from the app or email/phone OTP.
   */
  public confirmMFA(code: string): Observable<Response<{ confirmed: boolean; message: string }>> {
    return this.http.post<Response<{ confirmed: boolean; message: string }>>(
      `${environment.v2.baseUrl}auth/mfa/confirm`,
      { code: code },
      { context: new HttpContext().set(BypassErrorHandling, true) }
    )
  }

  /**
   * Returns the current user's recovery codes (Fortify).
   */
  public getMfaRecoveryCodes(): Observable<Response<{ recovery_codes: string[] }>> {
    return this.http.get<Response<{ recovery_codes: string[] }>>(`${environment.v2.baseUrl}auth/mfa/recovery-codes`, {
      context: new HttpContext().set(BypassErrorHandling, true)
    })
  }

  /**
   * Disables MFA.
   */
  public disableMFA(payload: { code?: string; recovery_code?: string }): Observable<Response<void>> {
    return this.http.post<Response<void>>(`${environment.v2.baseUrl}auth/mfa/disable`, payload ?? {}, { context: new HttpContext().set(BypassErrorHandling, true) })
  }

  /** Linked devices (server sessions) */
  public getSessions(): Observable<
    Response<{
      sessions: Array<{
        id: string
        ip_address: string | null
        user_agent: string | null
        last_activity: number
        last_activity_human: string
        is_current?: boolean
        location?: {
          city?: string | null
          region?: string | null
          country?: string | null
          map_query: string
          lat?: number | null
          lon?: number | null
        } | null
      }>
    }>
  > {
    return this.http.get<
      Response<{
        sessions: Array<{
          id: string
          ip_address: string | null
          user_agent: string | null
          last_activity: number
          last_activity_human: string
          is_current?: boolean
          location?: {
            city?: string | null
            region?: string | null
            country?: string | null
            map_query: string
            lat?: number | null
            lon?: number | null
          } | null
        }>
      }>
    >(`${environment.v2.baseUrl}auth/sessions`, { context: new HttpContext(), withCredentials: true })
  }

  public deleteSession(sessionId: string): Observable<Response<void>> {
    return this.http.delete<Response<void>>(`${environment.v2.baseUrl}auth/sessions/${encodeURIComponent(sessionId)}`, {
      context: new HttpContext(),
      withCredentials: true
    })
  }

  /** Deletes all sessions for user except current (server-side) */
  public deleteOtherSessions(): Observable<Response<void>> {
    return this.http.delete<Response<void>>(`${environment.v2.baseUrl}auth/sessions`, {
      context: new HttpContext(),
      withCredentials: true
    })
  }

  /** Passkeys (WebAuthn) */
  public listPasskeys(): Observable<
    Response<{
      passkeys: Array<{ id: number; name: string | null; created_at: string | null; last_used_at: string | null }>
    }>
  > {
    return this.http.get<
      Response<{
        passkeys: Array<{ id: number; name: string | null; created_at: string | null; last_used_at: string | null }>
      }>
    >(`${environment.v2.baseUrl}auth/passkeys`, { context: new HttpContext() })
  }

  public getPasskeyRegistrationOptions(): Observable<Response<{ request_id: string; publicKey: any }>> {
    return this.http.post<Response<{ request_id: string; publicKey: any }>>(`${environment.v2.baseUrl}auth/passkeys/registration/options`, {}, { context: new HttpContext() })
  }

  public verifyPasskeyRegistration(payload: {
    request_id: string
    credential: any
    name?: string | null
  }): Observable<Response<{ passkey: { id: number; name: string | null; created_at: string | null; last_used_at: string | null } }>> {
    return this.http.post<Response<{ passkey: { id: number; name: string | null; created_at: string | null; last_used_at: string | null } }>>(
      `${environment.v2.baseUrl}auth/passkeys/registration/verify`,
      payload,
      { context: new HttpContext() }
    )
  }

  public deletePasskey(passkeyId: number): Observable<Response<void>> {
    return this.http.delete<Response<void>>(`${environment.v2.baseUrl}auth/passkeys/${passkeyId}`, { context: new HttpContext() })
  }
}
